/**
 * FAQPage - Frequently Asked Questions with Schema.org markup
 *
 * SEO Target: /vanliga-fragor
 * Primary keywords: årsredovisning frågor, K2 K3 frågor, bokslut hjälp
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { usePricing } from "@/hooks/use-pricing";

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

// FAQs that need dynamic pricing - will be populated inside component
const getGeneralFaqs = (formattedPrice: string): FAQ[] => [
  {
    category: "Allmänt",
    question: "Vad är Kvanten?",
    answer:
      `Kvanten är en digital plattform för att skapa årsredovisningar enligt K2 och K3-regelverken. Du laddar upp din SIE-fil från bokföringsprogrammet, och vi genererar automatiskt din årsredovisning. Priset är ${formattedPrice} per företag och räkenskapsår.`,
  },
  {
    category: "Allmänt",
    question: "Vem får göra sin egen årsredovisning utan revisor?",
    answer:
      "Företag som uppfyller minst två av tre villkor under de senaste två åren får göra egen årsredovisning: högst 3 anställda (i medeltal), högst 1,5 miljoner kr i balansomslutning, eller högst 3 miljoner kr i nettoomsättning. Detta gäller majoriteten av svenska småföretag. Större företag måste ha revisor som granskar årsredovisningen.",
  },
  {
    category: "Allmänt",
    question: "Vad kostar det att göra årsredovisning med Kvanten?",
    answer:
      `Kvanten kostar ${formattedPrice} exkl. moms per företag och räkenskapsår. Detta inkluderar PDF i Bolagsverkets format, XML för digital inlämning, automatisk K2/K3-validering, digital aktiebok, årsstämmoprotokoll och 7 års arkivering. Jämfört med en traditionell byrå som tar 8 000-25 000 kr sparar du upp till 90%.`,
  },
];
// Static FAQs that don't need dynamic pricing
const staticFaqs: FAQ[] = [
  // K2/K3
  {
    category: "K2 & K3",
    question: "Vad är skillnaden mellan K2 och K3?",
    answer:
      "K2 är det förenklade regelverket för mindre företag med färre noter och enklare värderingsregler. K3 är huvudregelverket med obligatorisk kassaflödesanalys, uppskjuten skatt och mer detaljerade upplysningskrav. De allra flesta svenska aktiebolag kvalificerar för K2.",
  },
  {
    category: "K2 & K3",
    question: "Hur vet jag om mitt företag får använda K2?",
    answer:
      "K2 får användas av 'mindre företag' som inte överskrider mer än ett av dessa gränsvärden under de två senaste åren: 50 anställda, 40 MSEK i balansomslutning, 80 MSEK i nettoomsättning. Publika aktiebolag, moderföretag i större koncerner och (från 2026) BRF:er får inte använda K2.",
  },
  {
    category: "K2 & K3",
    question: "Stödjer Kvanten både K2 och K3?",
    answer:
      "Ja, Kvanten stödjer både K2 och K3. För K3 inkluderas kassaflödesanalys (obligatorisk) och uppskjuten skatt-beräkning. Priset är samma oavsett vilket regelverk du väljer.",
  },
  // Praktiskt
  {
    category: "Praktiskt",
    question: "Vad är en SIE-fil?",
    answer:
      "SIE (Standard Import Export) är ett svenskt standardformat för att överföra bokföringsdata mellan system. Du exporterar SIE4-fil från ditt bokföringsprogram (Fortnox, Visma, Spiris m.fl.) och laddar upp den till Kvanten. Filen innehåller kontoplan, saldon och transaktioner.",
  },
  {
    category: "Praktiskt",
    question: "Hur lång tid tar det att göra årsredovisning?",
    answer:
      "Med Kvanten tar det vanligtvis 15-30 minuter beroende på hur komplex din bokföring är. Systemet analyserar din SIE-fil automatiskt och guidar dig genom de uppgifter som behöver kompletteras manuellt (styrelseledamöter, verksamhetsbeskrivning m.m.).",
  },
  {
    category: "Praktiskt",
    question: "Kan jag testa utan att betala?",
    answer:
      "Ja, det är helt gratis att skapa konto, ladda upp SIE-fil, gå igenom hela wizarden och se förhandsvisning av din årsredovisning. Du betalar först när du vill exportera den färdiga årsredovisningen som PDF/XML.",
  },
  // Inlämning
  {
    category: "Inlämning",
    question: "Hur lämnar jag in årsredovisningen till Bolagsverket?",
    answer:
      "Kvanten genererar XML-fil i Bolagsverkets format. Du laddar ner filen och lämnar in den digitalt via Bolagsverkets e-tjänst 'Lämna in årsredovisningen digitalt'. Du behöver BankID för att signera inlämningen.",
  },
  {
    category: "Inlämning",
    question: "När måste årsredovisningen vara inlämnad?",
    answer:
      "Årsredovisningen ska lämnas in till Bolagsverket senast 7 månader efter räkenskapsårets slut. För företag med kalenderår (jan-dec) är deadline alltså 31 juli. Vid försenad inlämning tar Bolagsverket ut förseningsavgifter på 5 000-20 000 kr.",
  },
  {
    category: "Inlämning",
    question: "Måste styrelsen underteckna årsredovisningen?",
    answer:
      "Ja, årsredovisningen måste undertecknas av samtliga styrelseledamöter och eventuell VD innan den lämnas in. Med Kvanten kan ni signera digitalt med BankID.",
  },
  // Tekniskt
  {
    category: "Tekniskt",
    question: "Vilka bokföringsprogram stödjer ni?",
    answer:
      "Kvanten stödjer alla bokföringsprogram som kan exportera SIE4-filer, vilket inkluderar Fortnox, Visma, Spiris, Björn Lundén, SpeedLedger, 24SevenOffice och många fler. Om ditt program exporterar SIE4 fungerar det med Kvanten.",
  },
  {
    category: "Tekniskt",
    question: "Är mina uppgifter säkra?",
    answer:
      "Ja, all data lagras säkert i Sverige/EU med kryptering både vid överföring och lagring. Vi följer GDPR och svensk dataskyddslag. Lösenord lagras med säker hashning och vi har automatisk backup av all data.",
  },
  {
    category: "Tekniskt",
    question: "Hur länge sparas min årsredovisning?",
    answer:
      "Vi arkiverar din årsredovisning i 7 år enligt bokföringslagens krav. Under hela denna period kan du logga in och ladda ner dina dokument.",
  },
];

const categories = ["Alla", "Allmänt", "K2 & K3", "Praktiskt", "Inlämning", "Tekniskt"];

// Helper to generate all FAQs with dynamic pricing
const getAllFaqs = (formattedPrice: string): FAQ[] => [
  ...getGeneralFaqs(formattedPrice),
  ...staticFaqs,
];

export function FAQPage() {
  const [activeCategory, setActiveCategory] = useState("Alla");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const pricing = usePricing();

  // Generate FAQs with dynamic pricing
  const faqs = getAllFaqs(pricing.formattedPriceExclVat);

  const filteredFaqs =
    activeCategory === "Alla" ? faqs : faqs.filter((faq) => faq.category === activeCategory);

  const breadcrumbItems = [{ label: "Vanliga frågor" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Vanliga frågor", url: "/vanliga-fragor" },
  ]);

  // Generate FAQPage schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <PageMeta
        title="Vanliga frågor om Årsredovisning | FAQ | Kvanten"
        description="Få svar på vanliga frågor om årsredovisning, K2/K3-regelverk, SIE-filer och digital inlämning till Bolagsverket. Utförlig FAQ för småföretagare."
        keywords="årsredovisning frågor, K2 K3 frågor, SIE-fil hjälp, bokslut frågor, Bolagsverket inlämning"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/vanliga-fragor`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Vanliga frågor</h1>
            <p className="text-xl text-gray-600">
              Här hittar du svar på de vanligaste frågorna om årsredovisning, K2/K3-regelverk
              och hur Kvanten fungerar.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 text-gray-600">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Hittade du inte svaret?</h2>
          <p className="text-gray-600 mb-6">
            Kontakta vår support så hjälper vi dig. Vi svarar vanligtvis inom 24 timmar.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Kontakta oss
          </Link>
        </div>
      </section>
    </>
  );
}
